'use client';

import { usePathname } from 'next/navigation';
import { ExternalLink, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackCompanyLinkClick } from '@/lib/analytics';
import type {
  CompanyBasicResponse,
  CompanyContactResponse,
} from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { useLocale } from '@/hooks/use-locale';
import { LocaleLink } from '@/components/locale-link';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { KeenIcon } from '@/components/keenicons';

/** Social-link config — keeps icon/label/URL-key tuples so the card can iterate uniformly. */
const SOCIAL_CONFIG: Array<{
  key: keyof Pick<
    CompanyContactResponse,
    | 'linkedin_url'
    | 'facebook_url'
    | 'instagram_url'
    | 'twitter_url'
    | 'github_url'
    | 'youtube_url'
    | 'discord_url'
    | 'tiktok_url'
  >;
  renderIcon: () => React.ReactNode;
  label: string;
  imgSrc?: string;
  darkImgSrc?: string;
}> = [
  {
    key: 'linkedin_url',
    renderIcon: () => (
      <img
        src="/media/brand-logos/linkedin.svg"
        alt="LinkedIn"
        width={18}
        height={18}
        className="shrink-0"
      />
    ),
    label: 'LinkedIn',
    imgSrc: '/media/brand-logos/linkedin-2.svg',
  },
  {
    key: 'facebook_url',
    renderIcon: () => (
      <KeenIcon icon="facebook" style="outline" className="text-lg shrink-0" />
    ),
    label: 'Facebook',
    imgSrc: '/media/brand-logos/facebook.svg',
  },
  {
    key: 'instagram_url',
    renderIcon: () => (
      <KeenIcon icon="instagram" style="outline" className="text-lg shrink-0" />
    ),
    label: 'Instagram',
    imgSrc: '/media/brand-logos/instagram.svg',
  },
  {
    key: 'twitter_url',
    renderIcon: () => (
      <KeenIcon icon="twitter" style="outline" className="text-lg shrink-0" />
    ),
    label: 'X (Twitter)',
    imgSrc: '/media/brand-logos/x.svg',
    darkImgSrc: '/media/brand-logos/x-dark.svg',
  },
  {
    key: 'github_url',
    renderIcon: () => (
      <KeenIcon
        icon="github"
        style="outline"
        className="text-lg shrink-0 text-[#181717]"
      />
    ),
    label: 'GitHub',
    imgSrc: '/media/brand-logos/GitHub_Invertocat_Black.svg',
    darkImgSrc: '/media/brand-logos/GitHub_Invertocat_White.svg',
  },
  {
    key: 'youtube_url',
    renderIcon: () => (
      <KeenIcon icon="youtube" style="outline" className="text-lg shrink-0" />
    ),
    label: 'YouTube',
    imgSrc: '/media/brand-logos/youtube.svg',
  },
  {
    key: 'discord_url',
    renderIcon: () => <MessageCircle size={18} className="shrink-0" />,
    label: 'Discord',
    imgSrc: '/media/brand-logos/discord.svg',
  },
  {
    key: 'tiktok_url',
    renderIcon: () => (
      <KeenIcon icon="tiktok" style="outline" className="text-lg shrink-0" />
    ),
    label: 'TikTok',
    imgSrc: '/media/brand-logos/tiktok.svg',
  },
];

/**
 * Right-column contact card: website, primary email (gated), VAT number, social links.
 *
 * Merges `contact` (authenticated fetch) with `basicData` (guest SSR) — contact wins where present.
 * Emails are only rendered when `showEmails` is true; guests see a sign-in prompt instead.
 * Returns null when all fields are empty, or a fixed-height placeholder while data is still loading.
 */
export function ContactInformationCard({
  contact,
  basicData,
  showEmails,
  euId,
  className,
}: {
  contact: CompanyContactResponse | null;
  basicData: CompanyBasicResponse | null;
  showEmails: boolean;
  euId: string;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const pathname = usePathname();

  const domain = contact?.domain ?? basicData?.domain;
  // A holding / financing vehicle with no site of its own: the backend offers
  // the group company's site, labelled. Only ever shown instead of, never
  // beside, an own website.
  const groupSite = !domain ? (basicData?.group_website ?? null) : null;
  const ustIdnr = contact?.ust_idnr ?? basicData?.ust_idnr;
  const primaryEmail = showEmails ? (contact?.primary_email ?? null) : null;
  const emailCount = showEmails ? (contact?.email_count ?? 0) : 0;

  const websiteUrl = domain?.startsWith('http')
    ? domain
    : domain
      ? `https://${domain}`
      : null;

  const getSocialUrl = (key: string) =>
    (contact as Record<string, any>)?.[key] ??
    (basicData as Record<string, any>)?.[key] ??
    null;
  const hasSocial = SOCIAL_CONFIG.some((c) => getSocialUrl(c.key));
  const hasEmails = !!primaryEmail;
  const hasUst = !!ustIdnr?.trim();

  const hasAnyWebsite = !!websiteUrl || !!groupSite;

  const isEmpty =
    (!hasAnyWebsite && !hasSocial && !hasEmails && !hasUst && showEmails) ||
    (!hasAnyWebsite && !hasSocial && !hasUst && !showEmails);

  if (isEmpty && (contact !== null || basicData !== null)) {
    return null;
  }
  if (isEmpty) {
    // Still loading — reserve space to avoid CLS. Carries `className` too: it
    // stands in for the card as a grid item, so it needs the same mobile
    // `order-*` or the placeholder reserves space in the wrong slot.
    return <div className={cn('h-[120px]', className)} />;
  }

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <CardTitle as="h2">{t('companyDetail.contactTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {websiteUrl && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">
              Website:
            </span>
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:underline truncate flex items-center gap-1"
              onClick={() => trackCompanyLinkClick(euId, 'website')}
            >
              {domain}
              <ExternalLink size={12} className="shrink-0" />
            </a>
          </div>
        )}

        {!websiteUrl && groupSite && (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground shrink-0">
              {t('companyDetail.groupWebsite', 'Konzern-Website')}:
            </span>
            <a
              href={`https://${groupSite.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-primary hover:underline truncate flex items-center gap-1"
              onClick={() => trackCompanyLinkClick(euId, 'group_website')}
            >
              {groupSite.domain}
              <ExternalLink size={12} className="shrink-0" />
            </a>
            <span className="text-xs text-muted-foreground truncate">
              {t('companyDetail.groupWebsiteVia', 'über')}{' '}
              {groupSite.register_court ? (
                <LocaleLink
                  href={generateCompanySlug(
                    {
                      name: groupSite.company_name,
                      registerCourt: groupSite.register_court,
                      registerType: groupSite.register_type ?? undefined,
                      registerId: groupSite.register_id ?? undefined,
                    },
                    locale,
                  )}
                  className="hover:underline"
                >
                  {groupSite.display_name ?? groupSite.company_name}
                </LocaleLink>
              ) : (
                groupSite.display_name ?? groupSite.company_name
              )}
            </span>
          </div>
        )}

        {hasEmails && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">
              Email:
            </span>
            <a
              href={`mailto:${primaryEmail}`}
              className="text-sm font-medium text-primary hover:underline truncate flex items-center gap-1"
              onClick={() => trackCompanyLinkClick(euId, 'email')}
            >
              {primaryEmail}
              <ExternalLink size={12} className="shrink-0" />
            </a>
            {emailCount > 1 && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                +{emailCount - 1} {t('common.labels.more', 'more')}
              </span>
            )}
          </div>
        )}
        {!showEmails && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">
              Email:
            </span>
            <span
              className="text-xs text-muted-foreground italic"
              data-nosnippet
            >
              {t('companyDetail.signInToViewPrefix') && (
                <>{t('companyDetail.signInToViewPrefix')} </>
              )}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  trackSignupAndRedirect(pathname, 'contact_email', euId);
                }}
                className="underline not-italic hover:text-foreground transition-colors"
              >
                {t('companyDetail.signInToViewLink', 'Anmelden')}
              </a>
              {t('companyDetail.signInToViewSuffix') && (
                <> {t('companyDetail.signInToViewSuffix')}</>
              )}
            </span>
          </div>
        )}

        {hasUst && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground shrink-0">
              USt-IdNr.:
            </span>
            <span className="text-sm font-medium text-foreground">
              {ustIdnr}
            </span>
          </div>
        )}

        {hasSocial && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {SOCIAL_CONFIG.map(
              ({ key, renderIcon, label, imgSrc, darkImgSrc }) => {
                const url = getSocialUrl(key);
                if (!url) return null;
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center rounded-md p-1.5 hover:bg-muted/50 transition-opacity hover:opacity-80"
                    title={label}
                    onClick={() => trackCompanyLinkClick(euId, key)}
                  >
                    {imgSrc ? (
                      <>
                        <img
                          src={darkImgSrc ?? imgSrc}
                          alt={label}
                          className={cn(
                            'h-[18px] w-[18px] object-contain shrink-0',
                            darkImgSrc ? 'hidden dark:block' : '',
                          )}
                        />
                        {darkImgSrc && (
                          <img
                            src={imgSrc}
                            alt={label}
                            className="h-[18px] w-[18px] object-contain shrink-0 dark:hidden"
                          />
                        )}
                      </>
                    ) : (
                      renderIcon()
                    )}
                  </a>
                );
              },
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
